/**
 * Juice — render-only audiovisual feedback state (cheap-juice pass 1).
 *
 * Lives OUTSIDE `src/engine` ON PURPOSE: this file is NOT under the ESLint determinism
 * guard, so it may use `Math.sin` / `Math.random` freely. It holds PRESENTATION state
 * only — screenshake offset, squash/stretch, hit flash, screen flash, hitstop countdown —
 * and is driven entirely from `World.render()` + the main-loop owner.
 *
 * FIREWALL RULE: `World.update()` and every entity sim path MUST NEVER READ these fields.
 * A render-only value that leaks into something the sim consumes (position/velocity/logic
 * timer) silently turns presentation into simulation and breaks replay parity. Trigger
 * methods below ARE called from sim events — that is fine: a deterministic edge producing a
 * cosmetic response is safe as long as the response never feeds back into the step.
 *
 * Screenshake uses the trauma model (Squirrel Eiserloh, GDC 2016 "Juicing Your Cameras
 * With Math"): amplitude = trauma² so small hits barely shake while big hits keep headroom,
 * and trauma DECAYS smoothly rather than resetting so consecutive hits blend.
 *
 * See docs/research/juice/00-SYNTHESIS.md for the full rationale + tuning table.
 */
export class Juice {
  /** Accessibility: when true, shake + hit-flash + screen-flash + hitstop are suppressed
   *  (motion sickness / photosensitivity). Squash/stretch stays — it is gentle and not a
   *  vestibular/flash trigger. Toggled from main.ts (persisted in localStorage). */
  reducedFx = false;

  // ── screenshake (trauma model) ────────────────────────────────────────
  private trauma = 0;
  private noiseT = 0;
  shakeX = 0;
  shakeY = 0;
  private static readonly MAX_OFFSET = 11; // px at trauma = 1
  private static readonly DECAY = 1.5;     // trauma units drained per second

  // ── squash / stretch (player sprite scale multipliers) ────────────────
  squashX = 1;
  squashY = 1;
  private sVX = 0;
  private sVY = 0;
  private static readonly STIFF = 520; // spring stiffness  (ω ≈ 22.8)
  private static readonly DAMP = 22;   // spring damping    (ζ ≈ 0.48 → a little overshoot)

  // ── hit flash: player sprite white→red tint, 0..1 ─────────────────────
  private flash = 0;

  // ── screen flash: fullscreen white overlay alpha, 0..1 ────────────────
  screenFlash = 0;

  // ── hitstop: ms the loop owner should freeze the sim ──────────────────
  hitstopMs = 0;

  /** Add impact trauma (capped at 1). Amplitude is trauma², so scale the amount by event
   *  severity: block-hit ~0.12 · stomp ~0.15 · hurt ~0.35 · dash ~0.18 · death ~0.7. */
  addTrauma(amount: number): void {
    if (this.reducedFx) return;
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /** Set an instantaneous squash/stretch impulse; the spring recovers to (1, 1). */
  squash(sx: number, sy: number): void {
    this.squashX = sx;
    this.squashY = sy;
  }

  /** Flash the player sprite toward red for a hit (decays ~150 ms). */
  hitFlash(strength = 1): void {
    if (this.reducedFx) return;
    if (strength > this.flash) this.flash = strength;
  }

  /** Pulse a fullscreen white flash for a big event — death / clear (decays ~200 ms). */
  screenFlashPulse(strength = 1): void {
    if (this.reducedFx) return;
    if (strength > this.screenFlash) this.screenFlash = strength;
  }

  /** Request a freeze-frame of `ms` (the loop owner honours it; keep frequent events short). */
  hitstop(ms: number): void {
    if (this.reducedFx) return;
    if (ms > this.hitstopMs) this.hitstopMs = ms;
  }

  /** Advance every render-only effect by real time `dt` (seconds). Called once per render. */
  update(dt: number): void {
    const d = dt > 0.05 ? 0.05 : dt; // clamp a post-tab-out jump

    // screenshake — trauma² amplitude, value-noise offset (product of out-of-phase sines
    // so it rolls organically instead of a pure regular sine).
    if (this.trauma > 0) {
      this.noiseT += d;
      const amp = this.trauma * this.trauma * Juice.MAX_OFFSET;
      const t = this.noiseT;
      this.shakeX = amp * Math.sin(t * 89.0) * Math.sin(t * 31.7 + 1.3);
      this.shakeY = amp * Math.sin(t * 97.0 + 2.1) * Math.sin(t * 43.3 + 4.7);
      this.trauma -= Juice.DECAY * d;
      if (this.trauma < 0) this.trauma = 0;
    } else {
      this.shakeX = 0;
      this.shakeY = 0;
    }

    // squash spring back toward (1, 1) — underdamped for a satisfying "boing".
    const ax = (1 - this.squashX) * Juice.STIFF - this.sVX * Juice.DAMP;
    this.sVX += ax * d; this.squashX += this.sVX * d;
    const ay = (1 - this.squashY) * Juice.STIFF - this.sVY * Juice.DAMP;
    this.sVY += ay * d; this.squashY += this.sVY * d;

    // flashes decay linearly.
    if (this.flash > 0) { this.flash -= d * 6.5; if (this.flash < 0) this.flash = 0; }
    if (this.screenFlash > 0) { this.screenFlash -= d * 5.0; if (this.screenFlash < 0) this.screenFlash = 0; }
  }

  /** Player sprite tint (0xRRGGBB) for the current hurt-flash: white → red as flash rises.
   *  Tint only multiplies down, so a hit reads as a red-shifted sprite (drop G+B channels). */
  playerTint(): number {
    if (this.flash <= 0) return 0xffffff;
    const t = this.flash > 1 ? 1 : this.flash;
    const gb = Math.round(255 - (255 - 70) * t);
    return (0xff << 16) | (gb << 8) | gb;
  }
}
